import { NotificationAction } from '@api/models';

import type { Notification, NotificationType } from '@domain/models/notification';
import {
  HUB_NOTIFICATION_TYPES,
  type HubNotificationType,
  notificationPath
} from '@domain/models/notification/destination';
import type { NotificationItemData } from '@ui/components/features/notification/notificationMenu';

/**
 * Translator function type for notification translations
 * Uses next-intl's useTranslations return type signature
 */
export type NotificationTranslator = (key: string, params?: Record<string, string | number>) => string;

/**
 * Account mode for notification filtering.
 * - 'professional': Notifications relevant to mentor/professional mode
 * - 'learner': Notifications relevant to learner/personal mode
 */
export type NotificationAccountMode = 'professional' | 'learner';

const HUB_TYPES: readonly NotificationType[] = HUB_NOTIFICATION_TYPES;

/** The Hub notifications about a professional's own activities; the others reach Managers and admins. */
const HUB_PROFESSIONAL_TYPES: readonly HubNotificationType[] = [
  'HUB_PLACEMENT_BLOCKED',
  'HUB_ARCHIVED',
  'HUB_ADDRESS_CHANGED'
];

/**
 * Notification types that are only relevant in professional/mentor mode
 */
const PROFESSIONAL_NOTIFICATION_TYPES: NotificationType[] = [
  'BOOKING_BOOKED_MENTOR',
  'BOOKING_REMINDER_MENTOR',
  'BOOKING_CANCELLED_MENTOR',
  'BOOKING_UPDATED_MENTOR',
  'PROFILE_VALIDATED',
  'PROFILE_DECLINED',
  'ACTIVITY_VALIDATED',
  'ACTIVITY_DECLINED',
  'PROFESSIONAL_BLOCKED',
  'PROFESSIONAL_UNBLOCKED',
  'PAYOUT_COMPLETED',
  'PAYOUT_FAILED',
  'DISPUTE_OPENED',
  'STRIPE_ONBOARDING_INCOMPLETE',
  'REVIEW_RECEIVED_MENTOR',
  ...HUB_PROFESSIONAL_TYPES
];

/**
 * Hub notifications to Managers (who may have a learner-only account) and to admins, so they show in both modes
 */
const BOTH_MODES_NOTIFICATION_TYPES: NotificationType[] = HUB_TYPES.filter(
  (type) => !(HUB_PROFESSIONAL_TYPES as readonly NotificationType[]).includes(type)
);

/**
 * Notification types that are only relevant in learner/personal mode
 */
const LEARNER_NOTIFICATION_TYPES: NotificationType[] = [
  'BOOKING_BOOKED_LEARNER',
  'BOOKING_REMINDER_LEARNER',
  'BOOKING_CANCELLED_LEARNER',
  'BOOKING_UPDATED_LEARNER',
  'REFUND_PROCESSED',
  'REFUND_FAILED',
  'DISPUTE_ACCEPTED',
  'DISPUTE_REJECTED'
];

/**
 * Returns the account mode a notification belongs to.
 * Used to filter notifications based on the current user mode (professional vs learner).
 *
 * @param type - The notification type string
 * @returns 'professional' | 'learner'
 *
 * @example
 * ```ts
 * getNotificationAccountMode('BOOKING_BOOKED_MENTOR')   // 'professional'
 * getNotificationAccountMode('BOOKING_BOOKED_LEARNER')   // 'learner'
 * getNotificationAccountMode('PAYOUT_COMPLETED')         // 'professional'
 * ```
 */
export function getNotificationAccountMode(type: string): NotificationAccountMode {
  if (PROFESSIONAL_NOTIFICATION_TYPES.includes(type as NotificationType)) {
    return 'professional';
  }
  if (LEARNER_NOTIFICATION_TYPES.includes(type as NotificationType)) {
    return 'learner';
  }
  // Fallback: types ending in _MENTOR → professional, _LEARNER → learner
  if (type.endsWith('_MENTOR')) return 'professional';
  if (type.endsWith('_LEARNER')) return 'learner';
  // Unknown types default to learner (personal) so they always appear somewhere
  return 'learner';
}

/**
 * Filters notifications to only include those relevant to the current account mode.
 *
 * @param notifications - Array of domain notification objects
 * @param isProfessional - Whether the user is currently in professional mode
 * @returns Filtered array of notifications for the current mode
 */
export function filterNotificationsByAccountMode<T extends { type: string }>(
  notifications: T[],
  isProfessional: boolean
): T[] {
  const mode: NotificationAccountMode = isProfessional ? 'professional' : 'learner';
  return notifications.filter(
    (n) =>
      BOTH_MODES_NOTIFICATION_TYPES.includes(n.type as NotificationType) || getNotificationAccountMode(n.type) === mode
  );
}

/**
 * Supported notification types for translation
 */
/** Max characters for learner review preview in mentor notifications (title shows reviewer name) */
const REVIEW_COMMENT_PREVIEW_MAX = 50;

function truncateWithEllipsis(text: string, maxChars: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, maxChars).trimEnd()}…`;
}

const TRANSLATABLE_NOTIFICATION_TYPES: NotificationType[] = [
  'BOOKING_BOOKED_MENTOR',
  'BOOKING_BOOKED_LEARNER',
  'BOOKING_REMINDER_MENTOR',
  'BOOKING_REMINDER_LEARNER',
  'PROFILE_VALIDATED',
  'PROFILE_DECLINED',
  'ACTIVITY_VALIDATED',
  'ACTIVITY_DECLINED',
  'BOOKING_CANCELLED_MENTOR',
  'BOOKING_CANCELLED_LEARNER',
  'BOOKING_UPDATED_MENTOR',
  'BOOKING_UPDATED_LEARNER',
  'REVIEW_RECEIVED_MENTOR',
  ...HUB_TYPES
];

/** The action the domain chose with a notification's destination (`metadata.action`). */
function actionOf(metadata: Record<string, unknown> | undefined): NotificationAction | undefined {
  const action = metadata?.action;
  return Object.values<unknown>(NotificationAction).includes(action) ? (action as NotificationAction) : undefined;
}

/**
 * Maps domain notification type to UI notification type
 *
 * @param type - Domain notification type string
 * @returns UI notification type
 *
 * @example
 * ```ts
 * mapNotificationType('BOOKING_BOOKED_MENTOR') // returns 'confirmed'
 * ```
 */
export function mapNotificationType(type: string): NotificationItemData['type'] {
  const typeMap: Record<string, NotificationItemData['type']> = {
    BOOKING_BOOKED_MENTOR: 'confirmed',
    BOOKING_BOOKED_LEARNER: 'confirmed',
    BOOKING_REMINDER_MENTOR: 'reminder',
    BOOKING_REMINDER_LEARNER: 'reminder',
    BOOKING_UPDATED_MENTOR: 'info',
    BOOKING_UPDATED_LEARNER: 'info',
    PROFILE_VALIDATED: 'confirmed',
    PROFILE_DECLINED: 'cancelled',
    ACTIVITY_VALIDATED: 'confirmed',
    ACTIVITY_DECLINED: 'cancelled',
    BOOKING_CANCELLED_MENTOR: 'cancelled',
    BOOKING_CANCELLED_LEARNER: 'cancelled',
    PROFESSIONAL_BLOCKED: 'cancelled',
    PROFESSIONAL_UNBLOCKED: 'confirmed',
    PAYOUT_COMPLETED: 'confirmed',
    PAYOUT_FAILED: 'cancelled',
    DISPUTE_OPENED: 'info',
    DISPUTE_ACCEPTED: 'confirmed',
    DISPUTE_REJECTED: 'cancelled',
    REFUND_PROCESSED: 'confirmed',
    REFUND_FAILED: 'cancelled',
    STRIPE_ONBOARDING_INCOMPLETE: 'cancelled',
    REVIEW_RECEIVED_MENTOR: 'review'
  };

  return typeMap[type] ?? 'info';
}

/**
 * Translates notification content based on type and metadata
 *
 * @param notification - Domain notification object
 * @param t - Translator function from useTranslations('Notifications')
 * @returns Object with translated title and message
 *
 * @example
 * ```ts
 * const t = useTranslations('Notifications');
 * const { title, message } = getTranslatedNotificationContent(notification, t);
 * ```
 */
export function getTranslatedNotificationContent(
  notification: Notification,
  t: NotificationTranslator
): { title: string; message: string } {
  const notificationType = notification.type as NotificationType;
  const metadata = (notification.metadata as Record<string, unknown>) || {};

  // Check if this notification type has translations
  if (TRANSLATABLE_NOTIFICATION_TYPES.includes(notificationType)) {
    try {
      const str = (v: unknown): string => (typeof v === 'string' ? v : '');
      const num = (v: unknown, fallback: number): number => (typeof v === 'number' && !Number.isNaN(v) ? v : fallback);

      const rawComment = str(metadata.reviewComment);
      const reviewCommentForUi =
        notificationType === 'REVIEW_RECEIVED_MENTOR'
          ? truncateWithEllipsis(rawComment, REVIEW_COMMENT_PREVIEW_MAX)
          : rawComment;

      const translationParams: Record<string, string | number> = {
        activityTitle: str(metadata.activityTitle),
        hasActivityTitle: str(metadata.activityTitle) ? 'yes' : 'no',
        bookingDate: str(metadata.bookingDate),
        minutesUntil: num(metadata.minutesUntil, 10),
        learnerName: str(metadata.learnerName),
        professionalName: str(metadata.professionalName),
        hasProfessionalName: str(metadata.professionalName) ? 'yes' : 'no',
        rejectionReason: str(metadata.rejectionReason),
        cancellationReason: str(metadata.cancellationReason),
        reviewerName: str(metadata.reviewerName),
        reviewComment: reviewCommentForUi,
        hubName: str(metadata.hubName),
        outcome: str(metadata.outcome),
        reviewKind: str(metadata.reviewKind),
        reason: str(metadata.reason),
        hasReason: str(metadata.reason) ? 'yes' : 'no',
        previousAddress: str(metadata.previousAddress),
        newAddress: str(metadata.newAddress),
        hasAddresses: str(metadata.previousAddress) && str(metadata.newAddress) ? 'yes' : 'no'
      };

      const title = t(`content.${notificationType}.title`, translationParams);
      const message = t(`content.${notificationType}.message`, translationParams);

      return { title, message };
    } catch {
      // Fallback to stored values if translation fails
      const message =
        notificationType === 'REVIEW_RECEIVED_MENTOR'
          ? truncateWithEllipsis(notification.message, REVIEW_COMMENT_PREVIEW_MAX)
          : notification.message;
      return {
        title: notification.title,
        message
      };
    }
  }

  // Return stored values for unknown notification types
  return {
    title: notification.title,
    message: notification.message
  };
}

/**
 * Gets translated action text for a notification based on its metadata
 *
 * @param notification - Domain notification object
 * @param t - Translator function from useTranslations('Notifications')
 * @returns Translated action text or undefined if no action available
 */
export function getNotificationActionText(notification: Notification, t?: NotificationTranslator): string | undefined {
  const metadata = notification.metadata as Record<string, unknown> | undefined;
  const notificationType = notification.type as NotificationType;

  // Stripe onboarding incomplete → complete setup action
  if (notificationType === 'STRIPE_ONBOARDING_INCOMPLETE') {
    return t ? t('actions.completeSetup') : 'Complete Setup';
  }

  // Profile validated or declined → view profile action
  if (notificationType === 'PROFILE_VALIDATED' || notificationType === 'PROFILE_DECLINED') {
    return t ? t('actions.viewProfile') : 'View Profile';
  }

  // Activity validated or declined → view activity action
  if (notificationType === 'ACTIVITY_VALIDATED' || notificationType === 'ACTIVITY_DECLINED') {
    return t ? t('actions.viewActivity') : 'View Activity';
  }

  // Review received → verify review action
  if (notificationType === 'REVIEW_RECEIVED_MENTOR') {
    return t ? t('actions.verifyReview') : 'Verify Review';
  }

  if (notificationPath(metadata)) {
    const action = actionOf(metadata);
    if (action) return t ? t(`actions.${action}`) : action;
    return t ? t('actions.open') : 'Open';
  }

  // Booking notifications and reminders → view booking action
  if (metadata?.bookingId) {
    return t ? t('actions.viewBooking') : 'View Booking';
  }

  // Fallback for activity-related notifications
  if (metadata?.activityId) {
    return t ? t('actions.viewActivity') : 'View Activity';
  }

  return undefined;
}

/**
 * Transforms domain notification to UI notification format with translations
 *
 * @param notification - Domain notification object
 * @param locale - Locale for localized action links (for next/link)
 * @param t - Optional translator function for i18n support
 * @returns UI-ready notification object
 *
 * @example
 * ```ts
 * const t = useTranslations('Notifications');
 * const uiNotification = transformNotificationForUI(domainNotification, 'en', t);
 * ```
 */
export function transformNotificationForUI(
  notification: Notification,
  locale: string,
  t?: NotificationTranslator
): NotificationItemData {
  // Get translated content if translator is provided
  const { title, message } = t
    ? getTranslatedNotificationContent(notification, t)
    : { title: notification.title, message: notification.message };

  const type = notification.type as NotificationType;

  return {
    id: notification.id,
    type: mapNotificationType(notification.type),
    title,
    subtitle: message,
    timestamp: new Date(notification.createdAt).toISOString(),
    isRead: notification.read,
    actionText: getNotificationActionText(notification, t),
    // Use locale-aware path for action links (next/link)
    actionHref: getNotificationNavigationPath(notification, locale),
    /** Mentor review: CTA opens verify dialog — no href; render as button in NotificationItem */
    actionAsButton: type === 'REVIEW_RECEIVED_MENTOR'
  };
}

/**
 * Filters notifications by read status
 *
 * @param notifications - Array of UI notifications
 * @returns Array of unread notifications
 */
export function filterUnreadNotifications(notifications: NotificationItemData[]): NotificationItemData[] {
  return notifications.filter((n) => !n.isRead);
}

/**
 * Gets navigation path for a notification with locale
 *
 * @param notification - Domain notification object
 * @param locale - Current locale (e.g., 'en', 'fr')
 * @returns Localized navigation path or undefined
 *
 * @example
 * ```ts
 * getNotificationNavigationPath(notification, 'en') // '/en/bookings?bookingId=123'
 * getNotificationNavigationPath(notification, 'fr') // '/fr/activity/123'
 * getNotificationNavigationPath(notification, 'en') // '/en/settings' (for profile validated)
 * ```
 */
export function getNotificationNavigationPath(notification: Notification, locale: string): string | undefined {
  const metadata = notification.metadata as Record<string, unknown> | undefined;
  const notificationType = notification.type as NotificationType;

  // Stripe onboarding incomplete → go to payment settings
  if (notificationType === 'STRIPE_ONBOARDING_INCOMPLETE') {
    return `/${locale}/settings/payments`;
  }

  // Profile validated → go to the public professional page so the user can
  // see how their now-active profile looks to learners.
  if (notificationType === 'PROFILE_VALIDATED') {
    if (metadata?.professionalId) {
      return `/${locale}/professional/${metadata.professionalId}`;
    }
    return `/${locale}/settings/profile`;
  }

  // Profile declined → go to account/profile page so the user can edit & resubmit.
  if (notificationType === 'PROFILE_DECLINED') {
    return `/${locale}/settings/profile`;
  }

  // Activity validated or declined → go to activity detail page
  if ((notificationType === 'ACTIVITY_VALIDATED' || notificationType === 'ACTIVITY_DECLINED') && metadata?.activityId) {
    return `/${locale}/activity/${metadata.activityId}`;
  }

  // Review received → handled by dialog, no navigation
  if (notificationType === 'REVIEW_RECEIVED_MENTOR') {
    return undefined;
  }

  const path = notificationPath(metadata);
  if (path) return `/${locale}${path}`;

  // Booking notifications (mentor/learner) and reminders → go to booking detail
  if (metadata?.bookingId) {
    return `/${locale}/bookings?bookingId=${metadata.bookingId}`;
  }

  // Fallback for activity-related notifications without booking
  if (metadata?.activityId) {
    return `/${locale}/activity/${metadata.activityId}`;
  }

  return undefined;
}
